/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Mar 25 2024
 **/

import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { useAtomValue, atom } from "jotai";

import { JotaiStore } from "@/frameworks/libraries/commons";
import { useMiniChatPopupManager } from "../useMiniChatPopupManager";
import { useChatVisibility } from "../../chat/useChatRoom/private";
import { queryClientStatic } from "../../API/private";

type ChannelID = string;

type MinimizeDMChatItem = {
	type: "DM";
	users: { src?: string; name: string; userID: string }[];
};

type MinimizeSpaceChatItem = {
	type: "Space";
	spaceID: string;
	spaceName: string;
};

type SizeMinimizeChatItem = {
	width?: number;
	height?: number;
};

export type MinimizeChatItemType = {
	/**
	 * id - 채팅 channelID 입니다.
	 */
	id: ChannelID;
	size?: SizeMinimizeChatItem;
	new?: boolean;
	threadID?: string;
} & (MinimizeDMChatItem | MinimizeSpaceChatItem);

/**
 * @createAt `미니 채팅 팝업이 숨김 처리된 시점 입니다. 해당 필드를 기준으로 미니마이즈 채팅 리스트의 정렬 순서를 결정합니다.`
 */
export type MinimizeChatList = Record<ChannelID, MinimizeChatItemType & { createdAt: number }>;

const minimizeChatListAtom = atom<MinimizeChatList>({});

export default function useMinimizeChatList() {
	const { closeAll } = useChatVisibility();
	const { miniChatPopupActions } = useMiniChatPopupManager();

	const append = useAtomCallback(
		useCallback((_, set, item: MinimizeChatItemType) => {
			set(minimizeChatListAtom, (prev) => {
				const result = { ...prev };
				if (item.type === "DM") {
					result[item.id] = {
						id: item.id,
						type: "DM",
						createdAt: Date.now(),
						users: item.users ?? [],
						size: item?.size,
						new: item?.new || false
					};
				} else {
					result[item.id] = {
						id: item.id,
						createdAt: Date.now(),
						type: "Space",
						spaceName: item.spaceName ?? "",
						spaceID: item.spaceID,
						size: item?.size,
						new: item?.new || false
					};
				}

				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const appendAll = useAtomCallback(
		useCallback((_, set) => {
			set(minimizeChatListAtom, (prev) => {
				const result = { ...prev };
				const openChatList = miniChatPopupActions.getAll();
				openChatList.map((item) => {
					if (item.type === "DM") {
						const dmRoomList: WebServices.DirectMessage.List.Data[number][] = [
							...(
								queryClientStatic.getQueryData<
									Map<string, WebServices.DirectMessage.List.Data[number]>
								>(["getDMList"]) ?? new Map()
							).values()
						];

						const userList = dmRoomList
							.filter((list) => list.roomId === item.id)
							.map((val) => val.users)[0];

						const channelUserList =
							userList?.map((user) => ({
								name: user.name,
								src: user.profileImagePath,
								userID: user.id
							})) || [];

						result[item.id] = {
							id: item.id,
							type: "DM",
							createdAt: Date.now(),
							users: channelUserList ?? [],
							size: item?.size
						};
					} else {
						const spaceList: WebServices.SpaceChat.List.Data[number][] = [
							...(
								queryClientStatic.getQueryData<Map<string, WebServices.SpaceChat.List.Data[number]>>([
									"getSpaceChatList"
								]) ?? new Map()
							).values()
						];
						const spaceListName = spaceList
							.filter((spaceInfo) => spaceInfo.spaceId === item.spaceID)
							.map((val) => val.spaceName);

						result[item.id] = {
							id: item.id,
							createdAt: Date.now(),
							type: "Space",
							spaceName: spaceListName[0] ?? "",
							spaceID: item.spaceID ?? "",
							size: item?.size
						};
					}
				});
				closeAll();

				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const remove = useAtomCallback(
		useCallback((get, set, id: string, threadID?: string) => {
			set(minimizeChatListAtom, (prev) => {
				const result = { ...prev };

				const current = result[id];

				if (current) {
					if (threadID) {
						if (!current.threadID) {
							delete result[id];
						}
					} else {
						delete result[id];
					}
				}

				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const clear = useAtomCallback(
		useCallback((_, set) => {
			set(minimizeChatListAtom, () => {
				return {};
			});
		}, []),
		{ store: JotaiStore }
	);

	const messageRead = useAtomCallback(
		useCallback((_, set, id: string) => {
			set(minimizeChatListAtom, (prev) => {
				const result = { ...prev };
				if (result[id]) {
					result[id] = {
						...result[id],
						new: false
					};
				}
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const messageReadAll = useAtomCallback(
		useCallback((_, set) => {
			set(minimizeChatListAtom, (prev) => {
				const result = { ...prev };
				Object.keys(result).forEach((key) => {
					result[key] = {
						...result[key],
						new: false
					};
				});
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const minimizeChatListActions = { append, appendAll, remove, clear, messageRead, messageReadAll };

	function useMinimizeChatListRenderer() {
		return useAtomValue(minimizeChatListAtom, { store: JotaiStore });
	}

	return { minimizeChatListActions, useMinimizeChatListRenderer };
}
